import { FieldOptionBase, FieldTypeDisplay } from 'module/api/field/types';
import {
  SingleSelectField,
  SingleUserField,
} from 'module/types/filter-field/field/filterable-field';
import {
  isOptional,
  SelectFilterOperationTypesForLevelField,
  isOptionalForTaskBlockingStatus,
  SelectFilterOperationType,
  SelectFilterOperationTypes,
  SelectFilterOperationTypesForSprintField,
} from 'module/types/filter-field/filter/select';
import { SingleSelectFieldFilter } from 'module/types/filter-field/filter/single-select';
import { SingleUserFieldFilter } from 'module/types/filter-field/filter/single-user';
import { Form } from 'antd';
import { snakeCase } from 'lodash';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { CriteriaSelect } from './components';
import { MultiSelectEditor } from './multi-select-editor';
import { SingleSelectEditor } from './single-select-editor';
import { CyWrapper } from './wrappers/cy-wrapper';
import {
  EditorComponents,
  FieldEditorSelectMode,
} from '../../field-editors/types';

interface Props {
  filter: SingleSelectFieldFilter | SingleUserFieldFilter;
  options: FieldOptionBase[];
  components: EditorComponents;
  onChange: (filter: SingleSelectFieldFilter | SingleUserFieldFilter) => void;
  getFilterOperationTypeOptions?: (
    field: SingleSelectField | SingleUserField,
  ) => SingleSelectFieldFilter[] | SingleUserFieldFilter[];
  getOperationOptionRenderer?: (
    type: SelectFilterOperationType,
  ) => null | React.JSX.Element;
  type?: FieldEditorSelectMode;
}

export const getValue = (
  filter: SingleSelectFieldFilter | SingleUserFieldFilter,
): number[] => {
  switch (filter.type) {
    case SelectFilterOperationType.Is:
    case SelectFilterOperationType.IsNot:
    case SelectFilterOperationType.IsAtLeast:
    case SelectFilterOperationType.IsAtMost:
      if (filter.value == null) {
        return [];
      }
      if (Array.isArray(filter.value)) {
        return filter.value.length > 1 ? [filter.value[0]] : [];
      }

      return [filter.value];
    case SelectFilterOperationType.IsOneOf:
    case SelectFilterOperationType.IsNoneOf:
      if (filter.value == null) {
        return [];
      }
      if (Array.isArray(filter.value)) {
        return filter.value;
      }

      return [filter.value];
    default:
      return [];
  }
};

export const getSingleSelectDefaultOperationTypeOptions = (
  field: SingleSelectField | SingleUserField,
): SingleSelectFieldFilter[] | SingleUserFieldFilter[] => {
  if (field.fieldTypeDisplay === FieldTypeDisplay.Level) {
    return SelectFilterOperationTypesForLevelField.map(
      (type) =>
        ({
          field,
          type,
        }) as any as SingleSelectFieldFilter,
    );
  }

  return SelectFilterOperationTypes.filter((x) => {
    if (field.fieldName === 'taskDependencyStatus') {
      return !field.metaData?.required || !isOptionalForTaskBlockingStatus(x);
    }

    return !field.metaData?.required || !isOptional(x);
  }).map(
    (type) =>
      ({
        field,
        type,
      }) as any as SingleSelectFieldFilter,
  );
};

export const SingleSelectBaseCriteria: FC<React.PropsWithChildren<Props>> = ({
  filter,
  components,
  options,
  onChange,
  getFilterOperationTypeOptions = getSingleSelectDefaultOperationTypeOptions,
  getOperationOptionRenderer,
  type = 'basic',
}) => {
  const getFilterOperationType = (field) => {
    if (field.fieldName === 'sprint') {
      return SelectFilterOperationTypesForSprintField.map(
        (operationType) =>
          ({
            field,
            type: operationType,
          }) as SingleSelectFieldFilter,
      );
    }

    return getFilterOperationTypeOptions(field);
  };

  return (
    <>
      <Form.Item>
        <CriteriaSelect
          disabled={filter == null}
          value={filter?.type}
          onChange={(selected) => {
            const nextFilter = {
              ...filter,
              type: selected,
            } as any;
            const isNextSingle =
              selected === SelectFilterOperationType.Is ||
              selected === SelectFilterOperationType.IsNot ||
              selected === SelectFilterOperationType.IsAtLeast ||
              selected === SelectFilterOperationType.IsAtMost;
            const nextValue = getValue(nextFilter);

            onChange({
              ...nextFilter,
              value: isNextSingle ? nextValue[0] : nextValue,
            } as any);
          }}
          options={(filter?.field == null
            ? []
            : (getFilterOperationType(filter.field) as (
                | SingleSelectFieldFilter
                | SingleUserFieldFilter
              )[])
          ).map((option) => ({
            label: SelectFilterOperationType[option.type],
            value: option.type,
            content: (
              <CyWrapper filter={filter} option={option}>
                {getOperationOptionRenderer?.(option.type) ?? (
                  <FormattedMessage
                    id={`task_list.filters.${snakeCase(
                      SelectFilterOperationType[option.type],
                    )}`}
                  />
                )}
              </CyWrapper>
            ),
          }))}
        />
      </Form.Item>
      {filter != null &&
        (filter.type === SelectFilterOperationType.Is ||
          filter.type === SelectFilterOperationType.IsNot ||
          filter.type === SelectFilterOperationType.IsAtLeast ||
          filter.type === SelectFilterOperationType.IsAtMost ||
          filter.type === SelectFilterOperationType.IsOneOf ||
          filter.type === SelectFilterOperationType.IsNoneOf) && (
          <Form.Item>
            {filter.type === SelectFilterOperationType.IsOneOf ||
            filter.type === SelectFilterOperationType.IsNoneOf ? (
              <MultiSelectEditor
                field={filter.field}
                filterType={filter.type}
                components={components}
                options={options}
                value={getValue(filter)}
                onChange={(latest: number[]) =>
                  onChange({
                    ...filter,
                    value: latest as any,
                  })
                }
                type={type}
              />
            ) : (
              <SingleSelectEditor
                field={filter.field}
                components={components}
                filterType={filter.type}
                options={options}
                value={getValue(filter).pop() ?? null}
                onChange={(selected: number | null) =>
                  onChange({ ...filter, value: selected ?? undefined })
                }
                type={type}
              />
            )}
          </Form.Item>
        )}
    </>
  );
};
